import type { Schema } from '@/shared/api/contract';

/**
 * Straight from the contract: cubas.domain.schemas.supplier of the back (contract v10.48,
 * ADR 0043). **What a profile may not see comes empty** (ADR 0042): the maximum discount
 * without `see_max_discount`, the purchase (net price, cost, purchase discount, the columns
 * the platform does not use, the original file) without `see_purchase`. The screens hide
 * the column when the value cannot be there, and the counter mode hides it when it can.
 */
export type Supplier = Schema<'SupplierRead'>;
export type SupplierWrite = Schema<'SupplierWrite'>;
export type SupplierUpdate = Schema<'SupplierUpdate'>;
export type SupplierCondition = Schema<'SupplierConditionRead'>;
export type SupplierConditionWrite = Schema<'SupplierConditionWrite'>;
export type PriceList = Schema<'PriceListRead'>;
export type VersionBrief = Schema<'PriceListVersionBrief'>;
export type Version = Schema<'PriceListVersionRead'>;
export type ColumnsView = Schema<'TariffColumnsView'>;
export type ColumnsWrite = Schema<'TariffColumnsWrite'>;
export type Finding = Schema<'TariffFinding'>;
export type Summary = Schema<'TariffSummary'>;
export type TariffItem = Schema<'TariffItemRead'>;
export type TariffHit = Schema<'TariffHit'>;
export type TariffSearchResult = Schema<'TariffSearchResult'>;
export type TariffField = Schema<'TariffField'>;
export type VersionStatus = Schema<'PriceListStatus'>;
export type FromAttachment = Schema<'FromAttachmentRequest'>;
export type OriginalLink = Schema<'OriginalLink'>;

/** The order of the screen, the one `TariffField` has in the back. */
export const TARIFF_FIELDS: readonly TariffField[] = [
  'reference',
  'description',
  'list_price',
  'net_price',
  'ean',
  'family',
  'units_per_box',
  'price_unit',
];

export const FIELD_LABELS: Record<TariffField, string> = {
  reference: 'Referencia',
  description: 'Descripción',
  list_price: 'PVP de tarifa',
  net_price: 'Precio neto de compra',
  ean: 'EAN (código de barras)',
  family: 'Familia o grupo de descuento',
  units_per_box: 'Unidades por caja',
  price_unit: 'El precio es por (1, 100, 1000)',
};

/** Without these the back does not read the file: the reference and one of the prices. */
export function missingFields(mapping: Partial<Record<TariffField, number>>): string[] {
  const missing: string[] = [];
  if (mapping.reference === undefined) missing.push('la referencia');
  if (mapping.list_price === undefined && mapping.net_price === undefined) {
    missing.push('un precio (el PVP o el neto)');
  }
  return missing;
}

export const STATUS_LABELS: Record<VersionStatus, string> = {
  preparing: 'Mirando el fichero',
  needs_columns: 'Falta elegir las columnas',
  reading: 'Leyendo los artículos',
  draft: 'Lista para revisar',
  published: 'Vigente',
  superseded: 'Sustituida',
  discarded: 'Descartada',
  failed: 'No se pudo leer',
};

/** In the worker's hands: the screen asks again every few seconds. */
export const WORKING: ReadonlySet<VersionStatus> = new Set(['preparing', 'reading']);

/** Who proposed the columns, in a line over them. */
export const ORIGIN_LABELS: Record<NonNullable<ColumnsView['origin']>, string> = {
  model: 'Propuestas por el asistente mirando los títulos y las primeras filas.',
  titles: 'Propuestas por los títulos de las columnas.',
  template: 'Como se leyó la última vez esta tarifa.',
  person: 'Como las dejaste.',
};

/**
 * TAR-01…05 (`cubas.tariffs.importer` of the back), in words. The back sends a sentence
 * too (`error`), written for the person; this is the line over it.
 */
/** The failures that are not the file's: asked for again, they read as if nothing happened
 * (`RETRYABLE_CODES` of the back). */
export const RETRYABLE_FAILURES: ReadonlySet<string> = new Set(['TAR-04', 'TAR-05']);

export function tariffFailureTitle(code: string | null | undefined): string {
  switch (code) {
    case 'TAR-01':
      return 'El fichero no se pudo abrir';
    case 'TAR-02':
      return 'Las columnas elegidas no están en el fichero';
    case 'TAR-04':
      return 'No se pudo pedir la lectura';
    case 'TAR-05':
      return 'No se pudo traer el fichero guardado';
    default:
      return 'No se pudo leer la tarifa';
  }
}

/**
 * What the back accepts as a tariff (`SPREADSHEET_SUFFIXES`): the attachment of an email
 * offers «Cargar como tarifa» only for these. `.xlsm` is an Excel with macros (Cadena 88
 * sends theirs that way); the macros are never run.
 */
export const TARIFF_SUFFIXES = ['.xlsx', '.xlsm', '.xls', '.csv', '.txt'] as const;

export const TARIFF_ACCEPT = TARIFF_SUFFIXES.join(',');

/** The attachments that offer «Cargar como tarifa»: the same without `.txt`, which the back
 * reads as a CSV but which in an email is nearly always something else (a note, a log). An
 * odd supplier that sends its tariff as `.txt` is uploaded from «Documentos». */
const ATTACHMENT_SUFFIXES = TARIFF_SUFFIXES.filter((suffix) => suffix !== '.txt');

export function isTariffFile(filename: string | null | undefined): boolean {
  const lower = (filename ?? '').toLowerCase();
  return ATTACHMENT_SUFFIXES.some((suffix) => lower.endsWith(suffix));
}

/** `2026-01-15` → `15/01/2026`, without a `Date`: a day has no time zone to slip on. */
export function tariffDate(value: string | null | undefined): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value ?? '');
  return match ? `${match[3]}/${match[2]}/${match[1]}` : '—';
}

/** The domain of a sender, to find their supplier: `ventas@bosch.es` → `bosch.es`. */
export function domainOf(address: string | null | undefined): string | null {
  const at = (address ?? '').lastIndexOf('@');
  if (at < 0) return null;
  const domain = (address ?? '')
    .slice(at + 1)
    .trim()
    .toLowerCase()
    .replace(/>$/, '');
  return domain || null;
}

/** The big free providers, whatever the country: `gmail.com`, `hotmail.es`, `yahoo.co.uk`. */
const FREE_MAIL_NAMES =
  /^(gmail|googlemail|hotmail|outlook|live|msn|yahoo|ymail|icloud|me|aol|gmx|proton|protonmail|zoho|tutanota)\.[a-z]{2,3}(\.[a-z]{2})?$/;

/** The Spanish ones a small supplier still writes from. */
const FREE_MAIL_DOMAINS = new Set([
  'telefonica.net',
  'movistar.es',
  'terra.es',
  'ono.com',
  'ya.com',
  'wanadoo.es',
  'orange.es',
  'jazztel.es',
  'vodafone.es',
  'euskaltel.net',
  'telecable.es',
  'mundo-r.com',
  'mixmail.com',
  'eresmas.com',
  'teleline.es',
  'inicia.es',
]);

/**
 * A sender at one of these is a person, not a company: its domain says nothing about the
 * supplier. Saved as a supplier's, it would claim every Gmail that writes; matched, it would
 * choose that supplier for any tariff a Gmail sends.
 */
export function isFreeMail(domain: string | null | undefined): boolean {
  if (!domain) return false;
  return FREE_MAIL_NAMES.test(domain) || FREE_MAIL_DOMAINS.has(domain);
}

/** The supplier whose domains include the sender's, or one of its parents
 * (`pedidos.bosch.es` is Bosch's when Bosch says `bosch.es`). Never from a free mail: a
 * supplier somebody gave `gmail.com` would otherwise be chosen for every Gmail. */
export function supplierForSender(
  suppliers: readonly Supplier[],
  address: string | null | undefined,
): Supplier | null {
  const domain = domainOf(address);
  if (!domain || isFreeMail(domain)) return null;
  return (
    suppliers.find((supplier) =>
      (supplier.domains ?? []).some((each) => domain === each || domain.endsWith(`.${each}`)),
    ) ?? null
  );
}
/** What «Publicar» sends: the date and, since contract v10.50, the notice to the team. */
export type PublishRequest = Schema<'PublishRequest'>;
